import type {
  BotSequenceConfigEffective,
  BotSequenceConfigInterface,
  BotSequenceConfigValues,
  BotSequenceScopeFmsTypes,
  BotSequenceType
} from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

/** The org/group/location a scoped sequence-config read or write applies to. */
export interface SequenceConfigScope {
  orgId: number;
  groupId?: number;
  locationId?: number;
}

//LINK - apps/server/src/API/SequenceConfig/SequenceConfig.Controller.ts
/**
 * The NEW sequence engine's cascading config (SequenceConfig, ENG-2987). Scope
 * addressing is uniform: pass groupId OR locationId for a narrower scope; neither = the
 * org scope. Reads are inheritance-resolved server-side; writes hit exactly the
 * addressed scope.
 */
export class SequenceConfigAPI {
  /** Every raw config row of the org, all scopes. */
  static async getConfigs(orgId: number): Promise<BotSequenceConfigInterface[]> {
    return APIService.Server.get<BotSequenceConfigInterface[]>(`/sequence-config/org/${orgId}`);
  }

  /** The scope's effective config per sequence type, every field with its source scope. */
  static async getEffectiveConfigs(scope: SequenceConfigScope): Promise<BotSequenceConfigEffective[]> {
    return APIService.Server.get<BotSequenceConfigEffective[]>(`/sequence-config/org/${scope.orgId}/effective${scopeQuery(scope)}`);
  }

  /** PARTIAL update at the scope: omitted fields untouched, null clears back to inherit. */
  static async updateConfig(scope: SequenceConfigScope, sequenceType: BotSequenceType, values: BotSequenceConfigValues) {
    return APIService.Server.put<BotSequenceConfigInterface>(`/sequence-config/org/${scope.orgId}/${sequenceType}${scopeQuery(scope)}`, values);
  }

  /** Delete one row — the scope goes back to inheriting everything. */
  static async deleteConfig(orgId: number, configId: number): Promise<{ ok: boolean }> {
    return APIService.Server.delete<{ ok: boolean }>(`/sequence-config/org/${orgId}/config/${configId}`);
  }

  /** The FMS type behind every location in the scope, so the page can gate FMS-dependent controls on the capability matrix. */
  static async getScopeFmsTypes(scope: SequenceConfigScope): Promise<BotSequenceScopeFmsTypes> {
    return APIService.Server.get<BotSequenceScopeFmsTypes>(`/sequence-config/org/${scope.orgId}/fms-types${scopeQuery(scope)}`);
  }
}

function scopeQuery(scope: SequenceConfigScope): string {
  const queryParams = new URLSearchParams();
  if (scope.groupId != null) queryParams.append("groupId", String(scope.groupId));
  if (scope.locationId != null) queryParams.append("locationId", String(scope.locationId));
  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : "";
}
